import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain, Code2, LogOut, User, BookOpen, Trophy } from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Seed database first if needed
      await axios.post(`${API}/seed`, {}, { withCredentials: true });

      // Load courses
      const coursesRes = await axios.get(`${API}/courses`, { withCredentials: true });
      setCourses(coursesRes.data);

      // Load progress
      const progressRes = await axios.get(`${API}/progress`, { withCredentials: true });
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  const getCourseProgress = (courseId) => {
    const courseProgress = progress.filter(p => p.course_id === courseId);
    if (courseProgress.length === 0) return 0;
    const completed = courseProgress.filter(p => p.completed).length;
    return Math.round((completed / 10) * 100); // 10 lessons per course
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="glass-effect">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src="/images/robot-genie-logo.png" 
                alt="Kids Genie" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Kids Genie
                </span>
                <div className="text-xs font-medium text-gray-600 -mt-1">AI & Coding Academy</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Student/Parent Info Display */}
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <User className="w-5 h-5 text-gray-600" />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-800">
                    {user?.child_name || user?.name}
                  </span>
                  {user?.child_name && user?.parent_name && (
                    <span className="text-xs text-gray-500">
                      Parent: {user.parent_name}
                    </span>
                  )}
                </div>
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="rounded-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12 animate-slide-in-up">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.child_name?.split(' ')[0] || user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-base text-gray-600">
                {user?.child_name ? (
                  <>Ready to continue your learning adventure, young coder?</>
                ) : (
                  <>Ready to continue your learning journey?</>
                )}
              </p>
              {user?.child_name && user?.child_age && (
                <p className="text-sm text-indigo-600 mt-1">
                  🎂 Age {user.child_age} • Learning with Kids Genie!
                </p>
              )}
            </div>
            <button
              onClick={() => navigate('/my-progress')}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Trophy className="w-5 h-5" />
              View My Progress
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Courses</p>
                  <p className="text-3xl font-bold text-gray-900">{courses.length}</p>
                </div>
                <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Lessons Completed</p>
                  <p className="text-3xl font-bold text-gray-900">{progress.filter(p => p.completed).length}</p>
                </div>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">In Progress</p>
                  <p className="text-3xl font-bold text-gray-900">{progress.filter(p => !p.completed && p.progress_percentage > 0).length}</p>
                </div>
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                  <Code2 className="w-7 h-7 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Courses</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {courses.map((course) => {
              const progressPercent = getCourseProgress(course.id);
              const Icon = course.subject === 'AI' ? Brain : Code2;
              const gradientClass = course.subject === 'AI' 
                ? 'from-indigo-600 to-purple-600' 
                : 'from-purple-600 to-pink-600';

              return (
                <Card
                  key={course.id}
                  data-testid={`course-card-${course.subject.toLowerCase()}`}
                  className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                >
                  <div className="relative h-48">
                    <img
                      src={course.image_url}
                      alt={course.name}
                      className="w-full h-full object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${gradientClass} opacity-60`}></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-6 h-6 text-white" />
                        <span className="text-white font-medium">{course.weeks} Lessons</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{course.name}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm text-gray-600 mb-4">{course.description}</p>
                    
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-600">Progress</span>
                        <span className="text-xs font-semibold text-gray-900">{progressPercent}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${gradientClass} transition-all duration-500`}
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      </div>
                    </div>

                    <Button
                      data-testid={`start-course-btn-${course.subject.toLowerCase()}`}
                      onClick={() => navigate(`/course/${course.id}`)}
                      className={`w-full bg-gradient-to-r ${gradientClass} text-white hover:opacity-90 rounded-full transition-all duration-300`}
                    >
                      {progressPercent > 0 ? 'Continue Learning' : 'Start Course'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
